import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { scrollToId } from "../lib/scroll";
import { Menu, X, Languages } from "lucide-react";

const Navbar = () => {
  const { t, toggle } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  const links = [
    { label: t.nav.demo, onClick: () => goSection("demo") },
    { label: t.nav.calculator, onClick: () => goSection("calculator") },
    { label: t.nav.blog, to: "/blog" },
    { label: t.nav.howItWorks, to: "/how-it-works" },
    { label: t.nav.caseStudy, to: "/case-study" },
    { label: t.nav.about, to: "/about" },
  ];

  return (
    <header data-testid="main-nav" className="fixed top-0 z-50 w-full glass-panel border-x-0 border-t-0">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="font-display text-2xl font-extrabold tracking-tight">
          <span className="gold-text">Sawtly</span> <span className="text-zinc-400 text-lg">صوتلي</span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label}`} className="text-sm text-zinc-400 transition-colors duration-300 hover:text-white">
                {l.label}
              </Link>
            ) : (
              <button key={l.label} onClick={l.onClick} data-testid={`nav-${l.label}`} className="text-sm text-zinc-400 transition-colors duration-300 hover:text-white">
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button onClick={toggle} data-testid="lang-toggle" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-zinc-300 transition-colors duration-300 hover:border-gold/60 hover:text-gold">
            <Languages size={14} /> {t.common.langToggle}
          </button>
          <button onClick={() => goSection("deploy")} data-testid="nav-deploy-cta" className="btn-gold !px-6 !py-2.5 !text-xs">
            {t.nav.deploy}
          </button>
        </div>

        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="text-white lg:hidden" aria-label="menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="glass-panel border-x-0 px-6 pb-6 pt-2 lg:hidden">
          <div className="flex flex-col gap-4">
            {links.map((l) =>
              l.to ? (
                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-base text-zinc-300">{l.label}</Link>
              ) : (
                <button key={l.label} onClick={l.onClick} className="text-start text-base text-zinc-300">{l.label}</button>
              )
            )}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={toggle} data-testid="lang-toggle-mobile" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-zinc-300">
                <Languages size={14} /> {t.common.langToggle}
              </button>
              <button onClick={() => goSection("deploy")} data-testid="nav-deploy-cta-mobile" className="btn-gold !px-6 !py-2.5 !text-xs">{t.nav.deploy}</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
